import clsx from "clsx";
import { WORKBENCH_INSET_PANEL_CLASS } from "@/components/ui/workbench";

/** Shimmer bar for Mission Control list skeletons. */
function Bar({ className }: { className?: string }) {
  return (
    <div
      className={clsx("rounded-md bg-slate-200/80 animate-pulse dark:bg-white/10", className)}
      aria-hidden
    />
  );
}

/** Indeterminate progress strip for slow refetches (toolbar area). */
export function MissionControlLoadingBar({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <div
      className="pointer-events-none absolute inset-x-6 top-0 z-20 h-0.5 overflow-hidden rounded-full bg-white/5"
      role="progressbar"
      aria-label="Loading"
    >
      <div className="h-full w-1/3 animate-[mc-bar_1.4s_ease-in-out_infinite] rounded-full bg-indigo-400/80" />
    </div>
  );
}

/** Skeleton row matching ``MissionControlCard`` / task list layout. */
function ListRowSkeleton() {
  return (
    <div className="rounded-xl bg-white/[0.02] px-5 py-3 ring-1 ring-white/5">
      <div className="flex gap-3">
        <Bar className="h-9 w-9 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-2">
          <Bar className="h-4 w-[58%]" />
          <Bar className="h-3 w-[38%]" />
        </div>
        <div className="w-16 shrink-0 space-y-2">
          <Bar className="h-3 w-full" />
          <Bar className="h-5 w-14 rounded-full" />
        </div>
      </div>
    </div>
  );
}

export function TaskListSkeleton({
  count = 5,
  label = "Loading tasks…",
}: {
  count?: number;
  label?: string;
}) {
  return (
    <div
      className="px-1 py-2"
      data-testid="task-list-skeleton"
      aria-busy
      aria-label={label}
    >
      <p className="mb-3 text-center text-xs text-slate-500">{label}</p>
      <ul className="flex flex-col gap-2">
        {Array.from({ length: count }).map((_, i) => (
          <li key={i}>
            <ListRowSkeleton />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function MissionListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="p-3" data-testid="mission-list-skeleton" aria-busy>
      <ul className="flex flex-col gap-2">
        {Array.from({ length: count }).map((_, i) => (
          <li key={i}>
            <ListRowSkeleton />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ArtifactListSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="py-2" data-testid="artifact-list-skeleton" aria-busy>
      <ul className="flex flex-col gap-2">
        {Array.from({ length: count }).map((_, i) => (
          <li key={i}>
            <ListRowSkeleton />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PipelineBoardSkeleton({ columns = 4 }: { columns?: number }) {
  return (
    <div
      className="flex h-full min-w-fit gap-3 px-4 pb-4 pt-2"
      data-testid="pipeline-board-skeleton"
      aria-busy
    >
      {Array.from({ length: columns }).map((_, col) => (
        <div
          key={col}
          className={clsx("flex h-full w-72 shrink-0 flex-col ring-1 ring-slate-200/80 dark:ring-slate-800/80", WORKBENCH_INSET_PANEL_CLASS)}
        >
          <div className="border-b border-slate-200/80 px-3 py-2.5 dark:border-slate-800/60">
            <Bar className="mb-2 h-3 w-16" />
            <Bar className="h-4 w-28" />
          </div>
          <div className="space-y-2 p-2">
            <Bar className="h-20 w-full rounded-lg" />
            <Bar className="h-20 w-full rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}
